import React from "react";
import {
  Badge,
  Button,
  Modal,
  Spinner,
  Table,
} from "components/ui/BootstrapCompat";
import {
  FiCheck,
  FiCopy,
  FiEdit3,
  FiPlus,
  FiPlay,
  FiRefreshCw,
  FiSave,
} from "react-icons/fi";
import { DonateWidget } from "services/main/donate.service";
import type { CreateWidgetFormState } from "./types";
import { TextInput } from "./FormControls";
import {
  getWidgetId,
  getWidgetPublicRouteUrl,
  getWidgetTypeLabel,
} from "./widgetUtils";

type WidgetListScreenProps = {
  isWidgetTableLoading: boolean;
  enabledWidgetList: DonateWidget[];
  token: string;
  businessId: string;
  copiedUrl: string;
  createWidgetForm: CreateWidgetFormState;
  isCreateWidgetOpen: boolean;
  isCreateWidgetLoading: boolean;
  onRefresh: () => void;
  onOpenCreateWidgetModal: () => void;
  onCloseCreateWidgetModal: () => void;
  onCreateWidgetSubmit: () => void;
  onCreateWidgetFormChange: (patch: Partial<CreateWidgetFormState>) => void;
  onCopyText: (text: string) => void;
  onOpenWidgetEditor: (widget: DonateWidget) => void;
  onOpenWidgetTester: (widget: DonateWidget) => void;
};

export function WidgetListScreen({
  isWidgetTableLoading,
  enabledWidgetList,
  token,
  businessId,
  copiedUrl,
  createWidgetForm,
  isCreateWidgetOpen,
  isCreateWidgetLoading,
  onRefresh,
  onOpenCreateWidgetModal,
  onCloseCreateWidgetModal,
  onCreateWidgetSubmit,
  onCreateWidgetFormChange,
  onCopyText,
  onOpenWidgetEditor,
  onOpenWidgetTester,
}: WidgetListScreenProps) {
  return (
    <main className="min-h-full min-w-0">
      <div className="flex w-full flex-col gap-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <h1 className="m-0 text-xl font-black text-[#101828]">
              ລາຍການ Widget
            </h1>
            <p className="m-0 mt-1 text-xs font-medium text-[#667085]">
              ຈັດການ widget ກ່ອນເຂົ້າໄປປັບແຕ່ງ overlay.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline-primary"
              className="inline-flex min-h-[40px] items-center gap-2 rounded-lg px-3 text-sm font-bold"
              disabled={isWidgetTableLoading}
              onClick={() => onRefresh()}
            >
              {isWidgetTableLoading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <FiRefreshCw className="h-4 w-4" />
              )}
              ໂຫຼດໃໝ່
            </Button>
            <Button
              variant="primary"
              className="inline-flex min-h-[40px] items-center gap-2 rounded-lg px-4 text-sm font-bold"
              disabled={!token || !businessId}
              onClick={onOpenCreateWidgetModal}
            >
              <FiPlus className="h-4 w-4" />
              ສ້າງ Donate Alert
            </Button>
          </div>
        </div>

        <section className="overflow-hidden rounded-lg border border-[#dbe4ef] bg-white shadow-[0_8px_24px_rgba(16,24,40,0.04)]">
          <div className="overflow-x-auto">
            <Table hover className="mb-0 min-w-[980px] text-sm">
              <thead className="bg-[#f8fafc] text-xs font-black text-[#667085]">
                <tr>
                  <th className="w-[64px] px-3 py-3 text-left">#</th>
                  <th className="px-3 py-3 text-left">ຊື່ວິດເຈດ</th>
                  <th className="px-3 py-3 text-left">ລາຍລະອຽດ</th>
                  <th className="px-3 py-3 text-left">ລີ້ງ</th>
                  <th className="px-3 py-3 text-right">ເຄື່ອງມື</th>
                </tr>
              </thead>
              <tbody className="text-[#344054]">
                {isWidgetTableLoading ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-8 text-center">
                      <span className="inline-flex items-center gap-2 text-sm font-bold text-[#667085]">
                        <Spinner animation="border" size="sm" />
                        ກຳລັງໂຫຼດ widget...
                      </span>
                    </td>
                  </tr>
                ) : enabledWidgetList.length ? (
                  enabledWidgetList.map((widget, index) => {
                    const widgetId = getWidgetId(widget);
                    const widgetPublicUrl = getWidgetPublicRouteUrl(widget);

                    return (
                      <tr key={widgetId || `${widget.name}-${index}`}>
                        <td className="px-3 py-3 font-bold text-[#667085]">
                          {index + 1}
                        </td>
                        <td className="px-3 py-3">
                          <div className="font-black text-[#101828]">
                            {widget.name || "--"}
                          </div>
                          <div className="mt-0.5 text-[11px] font-medium text-[#98a2b3]">
                            ID: {widgetId || "--"}
                          </div>
                        </td>
                        <td className="max-w-[260px] px-3 py-3">
                          <span className="line-clamp-2 text-xs font-medium text-[#667085]">
                            {widget.description || "--"}
                          </span>
                        </td>

                        <td className="px-3 py-3">
                          <div className="flex max-w-[260px] items-center gap-2">
                            <span className="min-w-0 flex-1 truncate text-xs font-bold text-[#667085]">
                              {widgetPublicUrl || "--"}
                            </span>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-lg p-0"
                              disabled={!widgetPublicUrl}
                              title="Copy widget URL"
                              onClick={() => onCopyText(widgetPublicUrl)}
                            >
                              {widgetPublicUrl &&
                              copiedUrl === widgetPublicUrl ? (
                                <FiCheck className="h-3.5 w-3.5" />
                              ) : (
                                <FiCopy className="h-3.5 w-3.5" />
                              )}
                            </Button>
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="inline-flex items-center gap-1 rounded-lg text-xs font-bold"
                              onClick={() => onOpenWidgetTester(widget)}
                            >
                              <FiPlay className="h-3.5 w-3.5" />
                              ທົດສອບ
                            </Button>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="inline-flex items-center gap-1 rounded-lg text-xs font-bold"
                              onClick={() => onOpenWidgetEditor(widget)}
                            >
                              <FiEdit3 className="h-3.5 w-3.5" />
                              ປັບແຕ່ງ
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={8} className="px-3 py-10 text-center">
                      <div className="text-sm font-black text-[#344054]">
                        ຍັງບໍ່ມີ Donate Alert Widget
                      </div>
                      <div className="mt-1 text-xs font-medium text-[#667085]">
                        ກົດ “ສ້າງ Donate Alert” ເພື່ອເພີ່ມລາຍການໃໝ່.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </section>
      </div>

      <Modal
        show={isCreateWidgetOpen}
        onHide={onCloseCreateWidgetModal}
        keyboard={!isCreateWidgetLoading}
        centered
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            onCreateWidgetSubmit();
          }}
        >
          <Modal.Header closeButton={!isCreateWidgetLoading}>
            <Modal.Title>
              <div className="flex items-center gap-2">
                <FiPlus className="h-4 w-4" />
                ສ້າງ Donate Alert
              </div>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="grid gap-3">
              <TextInput
                label="ຊື່ Widget"
                value={createWidgetForm.name}
                onChange={(value) => onCreateWidgetFormChange({ name: value })}
              />
              <label className="block min-w-0">
                <span className="mb-1 block text-xs font-bold text-[#667085]">
                  ລາຍລະອຽດ
                </span>
                <textarea
                  value={createWidgetForm.description}
                  onChange={(event) =>
                    onCreateWidgetFormChange({
                      description: event.target.value,
                    })
                  }
                  className="min-h-[92px] w-full resize-none rounded-lg border border-[#dbe4ef] bg-[#f8fafc] px-3 py-2 text-sm font-bold text-[#344054] outline-none transition focus:border-[#168feb] focus:bg-white focus:ring-2 focus:ring-[#d9efff]"
                />
              </label>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="light"
              className="rounded-lg border border-[#dbe4ef] bg-white px-4 text-sm font-bold text-[#344054]"
              disabled={isCreateWidgetLoading}
              onClick={onCloseCreateWidgetModal}
            >
              ຍົກເລີກ
            </Button>
            <Button
              variant="primary"
              type="submit"
              className="inline-flex min-w-[112px] items-center justify-center gap-2 rounded-lg px-4 text-sm font-bold"
              disabled={isCreateWidgetLoading}
            >
              {isCreateWidgetLoading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <FiSave className="h-4 w-4" />
              )}
              ບັນທຶກ
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </main>
  );
}
